import { LifeBuoy, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChamadosControllerListar } from '@/api/generated/suporte/suporte';
import { NovoChamadoDialogo } from '@/features/suporte/components/NovoChamadoDialogo';
import {
  CLASSE_STATUS,
  CLASSE_URGENCIA,
  formatarRelativo,
  ROTULO_STATUS,
  ROTULO_URGENCIA,
  STATUS,
  URGENCIAS,
} from '@/features/suporte/rotulos';
import { INTERVALO_LISTA_MS } from '@/features/suporte/suporte.constants';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { rotaDoChamado } from '@/shared/lib/rotas';
import { hasPermission } from '@/shared/permissoes/hasPermission';
import { Button } from '@/shared/ui/button';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'TODOS';

/**
 * Chamados do ambiente.
 *
 * ⚠️ Do AMBIENTE, não só meus: o backend escopa por tenant, então numa clínica
 * com vários usuários todos veem os chamados da clínica. É o comportamento
 * certo (quem abriu pode estar de folga) e o filtro "só os meus" existe para
 * quem quiser a outra leitura.
 */
export function MeusChamadosPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  const [pagina, setPagina] = useState(1);
  const [status, setStatus] = useState<string>(TODOS);
  const [urgencia, setUrgencia] = useState<string>(TODOS);
  const [abrindo, setAbrindo] = useState(false);

  const podeAbrir = hasPermission(me?.permissoes, 'suporte.criar');

  const { data, isPending, isError, refetch } = useChamadosControllerListar(
    {
      pagina,
      porPagina: 20,
      ordenarPor: 'abertoEm',
      ordem: 'desc',
      ...(status !== TODOS ? { status: status as never } : {}),
      ...(urgencia !== TODOS ? { urgencia: urgencia as never } : {}),
    },
    { query: { refetchInterval: INTERVALO_LISTA_MS } },
  );

  const botaoAbrir = podeAbrir ? (
    <Button onClick={() => setAbrindo(true)}>
      <Plus className="mr-2 h-4 w-4" />
      Abrir chamado
    </Button>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 flex-wrap gap-2">
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              setPagina(1);
            }}
          >
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              {STATUS.map((s) => (
                <SelectItem key={s} value={s}>
                  {ROTULO_STATUS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={urgencia}
            onValueChange={(v) => {
              setUrgencia(v);
              setPagina(1);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Urgência" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todas as urgências</SelectItem>
              {URGENCIAS.map((u) => (
                <SelectItem key={u} value={u}>
                  {ROTULO_URGENCIA[u]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {botaoAbrir}
      </div>

      {isPending ? (
        <EstadoCarregando rotulo="Carregando chamados..." />
      ) : isError ? (
        <EstadoErro aoTentarDeNovo={() => void refetch()} />
      ) : data.itens.length === 0 ? (
        <EstadoVazio
          icone={LifeBuoy}
          titulo="Nenhum chamado por aqui"
          descricao="Quando algo não funcionar como esperado, abra um chamado — a conversa fica registrada aqui."
          acao={botaoAbrir}
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-brand/15">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Assunto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Urgência</TableHead>
                  <TableHead>Atualizado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((chamado) => (
                  <TableRow
                    key={chamado.id}
                    onClick={() => navigate(rotaDoChamado(chamado.id))}
                    className="cursor-pointer"
                  >
                    <TableCell className="font-mono text-xs">{chamado.codigo}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{chamado.titulo}</span>
                        {chamado.naoLidas > 0 && (
                          <span className="rounded-full bg-brand px-1.5 py-0.5 text-[10px] font-bold text-brand-foreground">
                            {chamado.naoLidas}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={cn('rounded border px-2 py-0.5 text-xs', CLASSE_STATUS[chamado.status])}>
                        {ROTULO_STATUS[chamado.status]}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={cn('rounded border px-2 py-0.5 text-xs', CLASSE_URGENCIA[chamado.urgencia])}>
                        {ROTULO_URGENCIA[chamado.urgencia]}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatarRelativo(chamado.ultimaMensagemEm ?? chamado.abertoEm)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Paginacao
            pagina={data.pagina}
            totalPaginas={data.totalPaginas}
            total={data.total}
            porPagina={data.porPagina}
            aoMudarPagina={setPagina}
          />
        </>
      )}

      <NovoChamadoDialogo
        aberto={abrindo}
        aoFechar={() => setAbrindo(false)}
        aoAbrirChamado={(chamado) => navigate(rotaDoChamado(chamado.id))}
      />
    </div>
  );
}
